import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatDate, formatBirthday } from "@/lib/dateUtils";
import { CuteLoader } from "@/components/CuteLoader";

export default function ProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProfile = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    try {
      const res = await api.get("/employee/profile", { signal: controller.signal });
      setProfile(res.data);
      setError(null);
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('Request timed out');
      } else {
        setError('Could not load profile');
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const calculateTenure = (hireDate, rehireDate = null) => {
    const startDate = rehireDate ? new Date(rehireDate) : new Date(hireDate);
    const now = new Date();
    const diffTime = Math.abs(now - startDate);
    const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365.25));
    const diffMonths = Math.floor((diffTime % (1000 * 60 * 60 * 24 * 365.25)) / (1000 * 60 * 60 * 24 * 30.44));
    
    if (diffYears > 0 && diffMonths > 0) {
      return `${diffYears} year${diffYears !== 1 ? 's' : ''}, ${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
    } else if (diffYears > 0) {
      return `${diffYears} year${diffYears !== 1 ? 's' : ''}`;
    } else {
      return `${diffMonths} month${diffMonths !== 1 ? 's' : ''}`;
    }
  };

  const formatBirthday = (birthday) => {
    // Moved to dateUtils
    return null;
  };

  if (loading) {
    return <CuteLoader />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF7F1] flex items-center justify-center p-6">
        <div className="text-center">
          <div className="text-[#9C6B4A] text-lg mb-4 font-serif">{error}</div>
          <button
            onClick={() => { setLoading(true); setError(null); loadProfile(); }}
            className="px-6 py-3 bg-[#9C6B4A] text-white rounded-lg hover:bg-[#8B5A39] transition font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const employee = profile?.employee || user;
  const isAshley = employee.name === "Ashley Assad" || employee.email?.includes("ashley");
  const tenure = employee.hire_date ? calculateTenure(employee.hire_date, employee.rehire_date) : null;
  const birthday = formatBirthday(employee.birthday);

  return (
    <div className="min-h-screen bg-[#FAF7F1] p-6 pb-24">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-serif text-[#231E1A] mb-2">My Profile</h1>
        <p className="text-sm text-[#9C6B4A] mb-6">Your information</p>
        
        <div className="bg-white rounded-lg p-6 mb-4 border border-[#EAE1D4]">
          <div className="flex items-center mb-6 pb-6 border-b border-[#EAE1D4]">
            <div className="w-20 h-20 shrink-0 rounded-full bg-[#9C6B4A] flex items-center justify-center text-white text-2xl font-serif">
              {(employee.display_name || employee.name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </div>
            <div className="ml-4">
              <h2 className="text-xl font-semibold text-[#231E1A]">{employee.display_name || employee.name}</h2>
              {employee.email && <p className="text-[#9C6B4A]">{employee.email}</p>}
            </div>
          </div>
          
          <div className="space-y-4">
            {employee.employment_role && (
              <div>
                <div className="text-sm text-[#9C6B4A]">Role</div>
                <div className="font-medium text-[#231E1A]">{employee.employment_role}</div>
              </div>
            )}
            
            {employee.hire_date && (
              <div>
                <div className="text-sm text-[#9C6B4A]">
                  {isAshley && employee.rehire_date ? "Original Hire Date" : "Hire Date"}
                </div>
                <div className="font-medium text-[#231E1A]">
                  {formatDate(employee.hire_date)}
                </div>
              </div>
            )}
            
            {isAshley && employee.rehire_date && (
              <div>
                <div className="text-sm text-[#9C6B4A]">Returned</div>
                <div className="font-medium text-[#231E1A]">
                  {formatDate(employee.rehire_date)}
                </div>
              </div>
            )}
            
            {tenure && (
              <div>
                <div className="text-sm text-[#9C6B4A]">
                  {isAshley && employee.rehire_date ? "Tenure since return" : "Tenure"}
                </div>
                <div className="font-medium text-[#231E1A]">{tenure}</div>
              </div>
            )}
            
            {birthday && (
              <div>
                <div className="text-sm text-[#9C6B4A]">Birthday</div>
                <div className="font-medium text-[#231E1A]">{birthday}</div>
              </div>
            )}

            {employee.pto_balance !== undefined && employee.pto_balance !== null && (
              <div>
                <div className="text-sm text-[#9C6B4A]">PTO Balance</div>
                <div className="font-medium text-[#231E1A]">{employee.pto_balance.toFixed(2)} hours</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}